<template>
  <div class="h-subl">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-subl__content">
      <!-- ===== Page head ===== -->
      <div class="h-subl__head">
        <div>
          <h1 class="h-subl__title">Subscriptions</h1>
          <p class="h-subl__sub">{{ subtitle }}</p>
        </div>
        <div class="h-subl__actions">
          <button type="button" class="h-subl__issue" @click="issueOpen = true">
            <svg viewBox="0 0 24 24" class="h-subl__issuei"><path d="M12 5v14M5 12h14"/></svg>
            Issue licence
          </button>
        </div>
      </div>

      <!-- ===== Issue-licence drawer ===== -->
      <HermesIssueLicenceDrawer
        v-model="issueOpen"
        :api-base="apiBase"
        @issued="onIssued"
      />

      <!-- ===== Error ===== -->
      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load subscriptions"
        :message="error"
        @retry="load"
      />

      <!-- ===== First run ===== -->
      <HermesEmptyState
        v-else-if="!loading && !rows.length"
        variant="first-run"
        title="No subscriptions yet"
        message="When a customer completes checkout for one of your plans, their subscription appears here — with its licence, payments and lifecycle actions."
      />

      <!-- ===== Table ===== -->
      <div v-else class="h-subl__card">
        <HermesTabulator
          :rows="rows"
          :columns="columns"
          :loading="loading"
          storage-key="admin-subscriptions"
          :initial-sort="{ column: 'created', dir: 'desc' }"
          @row-click="openDetail"
        />
      </div>
      <p v-if="!loading && rows.length" class="h-subl__hint">Select a row to open the subscription detail.</p>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesSubscriptionsList — Phase 3 lean subscriptions list island. Replaces the
// old 6-tab dashboard's `subscriptions` tab: a HermesTabulator over the vendor's
// subscriptions (GET /admin/subscriptions) whose rows link through to the detail
// island at /admin/subscriptions/{id}. Money/status are formatted inline (the
// Tabulator engine renders cells outside scoped CSS, so badges use inline styles).
export default {
  name: 'HermesSubscriptionsList',
  props: {
    // Framework binds Page setProperty() values as top-level props; componentData
    // kept as a fallback.
    apiBase: { type: String, default: '/api/v1' },
    vendorName: { type: String, default: '' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      rows: [],
      loading: true,
      error: null,
      issueOpen: false,
      crumbs: [{ label: 'Commerce' }, { label: 'Subscriptions' }],
      columns: [
        { key: 'id', label: 'ID', width: 78, format: (v) => `<span style="font-family:var(--h-font-mono);color:var(--h-text-secondary)">#${v}</span>` },
        { key: 'end_user_email', label: 'Customer' },
        { key: 'plan_name', label: 'Plan', format: (v, row) => `${v || '<span style="color:var(--h-text-tertiary)">—</span>'}${this.grantChip(row)}` },
        { key: 'plan_price', label: 'Price', hozAlign: 'right', width: 120, sorter: 'number', format: (v, row) => this.money(v, row.plan_currency) },
        { key: 'status', label: 'Status', width: 130, format: (v) => this.badge(v) },
        { key: 'current_period_end', label: 'Renews', width: 130, sorter: 'number', format: (v) => this.date(v) },
        { key: 'created', label: 'Created', width: 130, sorter: 'number', format: (v) => this.date(v) },
      ],
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const n = this.rows.length;
      const live = this.rows.filter((r) => ['active', 'trialing', 'past_due', 'grace_period'].includes(r.status)).length;
      return `${n} subscription${n === 1 ? '' : 's'} · ${live} live`;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/subscriptions`);
        this.rows = (data.items || []).map((r) => ({ ...r, plan_price: r.plan_price != null ? Number(r.plan_price) : null }));
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load subscriptions${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    openDetail(row) {
      const id = row?.id;
      if (!id) return;
      if (this.$router) this.$router.push(`/admin/subscriptions/${id}`);
      else window.location.href = `/admin/subscriptions/${id}`;
    },
    onIssued() {
      // Refresh the list so the new grant appears; the drawer keeps showing the
      // one-time token until the operator dismisses it.
      this.load();
    },
    money(v, currency) {
      if (v === null || v === undefined || v === '') return '<span style="color:var(--h-text-tertiary)">—</span>';
      const sym = { GBP: '£', USD: '$', EUR: '€' }[currency] || '';
      const n = Number(v);
      const txt = `${sym}${Number.isNaN(n) ? v : n.toFixed(2)}`;
      return `<span style="font-family:var(--h-font-mono);font-variant-numeric:tabular-nums">${txt}</span>`;
    },
    date(v) {
      if (!v) return '<span style="color:var(--h-text-tertiary)">—</span>';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    grantChip(row) {
      const g = row && row.grant_type;
      if (!g) return '';
      // violet trial · gold lifetime · neutral comp — mirrors the detail badges.
      const styles = {
        trial: 'color:var(--h-violet-text,#6D28D9);background:var(--h-violet-bg,#F5F3FF);border-color:var(--h-violet-border,#DDD6FE)',
        lifetime: 'color:var(--h-apexl-gold-dark,#8B7355);background:var(--h-apexl-gold-light,#F6F1E7);border-color:var(--h-apexl-gold,#C9A227)',
        comp: 'color:var(--h-neutral-text,#475569);background:var(--h-neutral-bg,#F1F5F9);border-color:var(--h-neutral-border,#E2E8F0)',
      };
      const label = { trial: 'Trial', lifetime: 'Lifetime', comp: 'Comp' }[g] || g;
      const lock = row.is_single_use
        ? ' <span title="single-use" style="font-size:10px">🔒</span>'
        : '';
      return ` <span style="display:inline-flex;align-items:center;gap:3px;margin-left:7px;padding:2px 7px;border-radius:999px;font-size:10.5px;font-weight:600;border:1px solid transparent;${styles[g] || styles.comp}">${label}${lock}</span>`;
    },
    badge(status) {
      const map = {
        active: 'success', paid: 'success', valid: 'success',
        trialing: 'violet',
        past_due: 'warning', grace_period: 'danger',
        cancelled: 'neutral', expired: 'neutral', suspended: 'danger',
      };
      const role = map[status] || 'neutral';
      const s = (status || '').replace(/_/g, ' ');
      return `<span style="display:inline-flex;align-items:center;gap:6px;padding:3px 9px;border-radius:999px;font-size:11.5px;font-weight:600;border:1px solid var(--h-${role}-border);background:var(--h-${role}-bg);color:var(--h-${role}-text)">`
        + `<span style="width:6px;height:6px;border-radius:50%;background:var(--h-${role === 'violet' ? 'violet' : role})"></span>${s}</span>`;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-subl { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-subl__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-subl__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-subl__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-subl__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-subl__actions { display: flex; align-items: center; gap: 10px; flex: none; }
.h-subl__issue { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; background: var(--h-accent, #0EA5E9); color: #fff; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-subl__issue:hover { background: var(--h-accent-hover, #0284C7); }
.h-subl__issue:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-subl__issuei { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }
.h-subl__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-subl__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-subl__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
.h-subl__hint { font-size: 12.5px; color: var(--h-text-tertiary, #94A3B8); margin: 10px 2px 0; }
</style>
<style>
/* Row-click affordance (Tabulator cells render outside scoped CSS). */
.h-subl .hermes-tabulator .tabulator .tabulator-row { cursor: pointer; }
</style>
